// O radar da EQUIPE (05/10/2026) — a régua pura que junta as quatro fontes.
//
// Pedido do Lucas: "o Meu Radar que o Gustavo criou tem que ser visível para
// toda a equipe comercial e integrado ao pipeline das licitações. No fim, é a
// mesma coisa, só muda a visualização. O Meu Radar também deve mostrar as
// atividades agendadas das oportunidades privadas, passível de filtragem."
//
// O servidor (`radar_painel`) devolve as fontes CRUAS, cada uma com a sua
// permissão; aqui elas viram cartões. Quatro regras, todas com teste
// (equipe.test.ts):
//
// 1. UM cartão por coisa. O mesmo edital no radar do Gustavo, no meu e no
//    quadro do pipeline é UM cartão: o meu item, se eu tiver; senão o de quem
//    pôs primeiro. Os outros donos viram "também no radar de". Negócio idem.
// 2. Só o que é MEU se mexe (editar, tirar, arrastar). O cartão de um colega
//    é leitura — as escritas do servidor confirmam o dono de qualquer jeito.
// 3. O prazo que manda depende da etapa do quadro: até a Proposta é o do
//    radar (o da fonte, ou o informado à mão); da Sessão em diante o prazo da
//    proposta já passou e vale o próximo prazo do quadro — senão a Habilitação
//    aparece "vencida há 20 dias". Data informada à mão no radar sempre ganha.
// 4. O ⏰ (dia do aviso por e-mail) só existe no MEU item com o prazo do MEU
//    radar: é o aviso que chega para mim. Do colega, do quadro e da atividade
//    não há ⏰ — o aviso deles vai para outra pessoa.
//
// Sem antd e sem React: o jest do CRA5 não carrega o antd v6.
import type { ItemRadar, TipoItem } from './api';

export type FontePainel = 'radar' | 'pipeline' | 'atividade';
export type TipoPainel = TipoItem | 'atividade';

/** Uma licitação aberta no quadro do pipeline, como o `radar_painel` manda. */
export interface LicitacaoDoQuadro {
  oportunidade_id: number;
  numero_edital: string | null;
  numero_processo: string | null;
  orgao: string | null;
  uf: string | null;
  cidade: string | null;
  modalidade: string | null;
  objeto: string | null;
  valor_estimado: number | string | null;
  situacao: string | null;
  fonte: string | null;
  link_edital: string | null;
  status_interno: string;
  etapa_rotulo: string;
  fit_score: number | null;
  area_tecnica: string | null;
  responsavel_email: string | null;
  etapa_desde: string | null;
  eventos_unack_count: number | null;
  /** O prazo que manda agora — parede BRT, sem offset. */
  prazo: string | null;
  dias_restantes: number | null;
  /** Vocabulário do `SQL_ORIGEM_PRAZO`, ou 'pipeline:<tipo>' (sessão, recurso…). */
  origem_prazo: string | null;
}

/** Atividade agendada e em aberto de um negócio privado. */
export interface AtividadeDoNegocio {
  atividade_id: string;
  titulo: string;
  atividade_tipo: string | null;
  atividade_status: string;
  prioridade: string | null;
  criado_por: string | null;
  /** `data_vencimento` — parede BRT, sem offset. */
  prazo: string | null;
  dias_restantes: number | null;
  negocio_id: number | null;
  negocio_titulo: string | null;
  negocio_status: string | null;
  negocio_valor: number | string | null;
  negocio_responsavel: string | null;
  negocio_cliente: string | null;
  responsaveis: string[];
}

export interface PainelRadar {
  eu: string;
  radar: Array<ItemRadar & { meu: boolean }>;
  /** null = sem permissão para o quadro (≠ quadro vazio). */
  pipeline: LicitacaoDoQuadro[] | null;
  /** null = sem permissão para os negócios (≠ nenhuma atividade). */
  atividades: AtividadeDoNegocio[] | null;
  /** e-mail → nome do cadastro de profissionais. */
  pessoas: Record<string, string>;
}

/** Um cartão do radar da equipe. Os campos de prazo têm o MESMO nome dos do
 *  ItemRadar, para `classificarPrazoRadar` servir aos dois sem cópia. */
export interface ItemPainel {
  /** Única entre as fontes (os ids de radar, edital e atividade se repetem). */
  chave: string;
  fonte: FontePainel;
  tipo: TipoPainel;
  /** É do MEU radar — o único que se edita, tira ou arrasta. */
  meu: boolean;
  rotulo: string;
  prazo: string | null;
  dias_restantes: number | null;
  /** 'manual' | vocabulário da régua | 'pipeline:<tipo>' | 'atividade'. */
  origem_prazo: string | null;
  /** Dia do MEU aviso (⏰). null fora do meu radar — regra 4. */
  alerta_em: string | null;
  /** Dono do item de radar exibido (null para quadro e atividade). */
  dono_email: string | null;
  /** Os OUTROS que têm o mesmo edital/negócio no radar. */
  acompanhantes: string[];
  /** Responsável no quadro (licitação) ou do negócio (atividade). */
  responsavel_email: string | null;
  /** Todo mundo ligado ao cartão — é o que o filtro "Pessoa" procura. */
  pessoas: string[];
  etapa: string | null;
  etapa_rotulo: string | null;
  oportunidade_id: number | null;
  negocio_id: number | null;
  /** A linha do radar exibida (editar, anotações, análise da IA). */
  radar: ItemRadar | null;
  /** Os ids de TODAS as linhas de radar juntadas neste cartão (a exibida
   *  primeiro) — o link do aviso (`?item=`) de qualquer dono acha o cartão. */
  radar_ids: number[];
  /** Só para fonte 'atividade'. */
  atividade: AtividadeDoNegocio | null;
  /** Só para licitação no quadro. */
  quadro: LicitacaoDoQuadro | null;
}

/** Etapas do quadro em que o prazo que manda ainda é o da PROPOSTA. Espelha
 *  ETAPAS_PRAZO_DA_PROPOSTA de handlers/radarPessoal.js. */
export const ETAPAS_PRAZO_DA_PROPOSTA = ['avaliando', 'preparacao', 'proposta', 'em_proposta'];

const minusculo = (s: string | null | undefined) => String(s || '').trim().toLowerCase();

function unicos(lista: Array<string | null | undefined>): string[] {
  const vistos = new Set<string>();
  const saida: string[] = [];
  for (const bruto of lista) {
    const e = minusculo(bruto);
    if (e && !vistos.has(e)) { vistos.add(e); saida.push(e); }
  }
  return saida;
}

function rotuloDoEdital(l: { numero_edital: string | null; numero_processo: string | null; oportunidade_id: number }): string {
  return String(l.numero_edital || '').trim() || String(l.numero_processo || '').trim() || `edital #${l.oportunidade_id}`;
}

function deRadar(
  exibida: ItemRadar & { meu: boolean },
  juntadas: Array<ItemRadar & { meu: boolean }>,
  quadro: LicitacaoDoQuadro | undefined,
): ItemPainel {
  const outros = juntadas.map(l => l.dono_email);
  // Regra 3: da Sessão em diante, o prazo da proposta já passou — vale o do
  // quadro. Data informada à mão no radar ganha sempre (foi alguém que disse).
  const prazoDoQuadro = !!quadro && !exibida.prazo_manual
    && !ETAPAS_PRAZO_DA_PROPOSTA.includes(quadro.status_interno);
  const prazo = prazoDoQuadro ? quadro!.prazo : exibida.prazo;
  return {
    chave: `radar:${exibida.id}`,
    fonte: 'radar',
    tipo: exibida.tipo,
    meu: !!exibida.meu,
    rotulo: exibida.rotulo,
    prazo,
    dias_restantes: prazoDoQuadro ? quadro!.dias_restantes : (exibida.dias_restantes ?? null),
    origem_prazo: prazoDoQuadro ? quadro!.origem_prazo : (exibida.prazo_manual ? 'manual' : exibida.origem_prazo),
    // Regra 4: o ⏰ é o MEU aviso, do prazo do MEU radar.
    alerta_em: exibida.meu && !prazoDoQuadro ? exibida.alerta_em : null,
    dono_email: minusculo(exibida.dono_email) || null,
    acompanhantes: unicos(outros),
    responsavel_email: quadro ? minusculo(quadro.responsavel_email) || null : null,
    pessoas: unicos([exibida.dono_email, ...outros, quadro?.responsavel_email]),
    etapa: quadro ? quadro.status_interno : null,
    etapa_rotulo: quadro ? quadro.etapa_rotulo : null,
    oportunidade_id: exibida.oportunidade_id ?? null,
    negocio_id: exibida.negocio_id ?? null,
    radar: exibida,
    radar_ids: [exibida.id, ...juntadas.map(l => l.id)],
    atividade: null,
    quadro: quadro || null,
  };
}

function doQuadro(l: LicitacaoDoQuadro): ItemPainel {
  return {
    chave: `quadro:${l.oportunidade_id}`,
    fonte: 'pipeline',
    tipo: 'licitacao',
    meu: false,
    rotulo: rotuloDoEdital(l),
    prazo: l.prazo,
    dias_restantes: l.dias_restantes,
    origem_prazo: l.origem_prazo,
    alerta_em: null,
    dono_email: null,
    acompanhantes: [],
    responsavel_email: minusculo(l.responsavel_email) || null,
    pessoas: unicos([l.responsavel_email]),
    etapa: l.status_interno,
    etapa_rotulo: l.etapa_rotulo,
    oportunidade_id: l.oportunidade_id,
    negocio_id: null,
    radar: null,
    radar_ids: [],
    atividade: null,
    quadro: l,
  };
}

function daAtividade(a: AtividadeDoNegocio): ItemPainel {
  // Sem ninguém atribuído, quem criou responde por ela.
  const gente = a.responsaveis && a.responsaveis.length ? a.responsaveis : [a.criado_por];
  return {
    chave: `atividade:${a.atividade_id}`,
    fonte: 'atividade',
    tipo: 'atividade',
    meu: false,
    rotulo: String(a.titulo || '').trim() || 'Atividade',
    prazo: a.prazo,
    dias_restantes: a.dias_restantes,
    origem_prazo: 'atividade',
    alerta_em: null,
    dono_email: null,
    acompanhantes: [],
    responsavel_email: minusculo(gente[0]) || null,
    pessoas: unicos(gente),
    etapa: null,
    etapa_rotulo: null,
    oportunidade_id: null,
    negocio_id: a.negocio_id,
    radar: null,
    radar_ids: [],
    atividade: a,
    quadro: null,
  };
}

/** As quatro fontes → cartões. Regra 1: um cartão por edital e por negócio. */
export function montarPainel(p: PainelRadar | null | undefined): ItemPainel[] {
  if (!p) return [];
  const quadroPorEdital = new Map<number, LicitacaoDoQuadro>();
  for (const l of p.pipeline || []) quadroPorEdital.set(Number(l.oportunidade_id), l);

  // Agrupa o radar por ALVO: o mesmo edital (ou negócio) no radar de várias
  // pessoas. Lembrete livre não tem alvo — cada um é o seu.
  const porAlvo = new Map<string, Array<ItemRadar & { meu: boolean }>>();
  for (const r of p.radar || []) {
    const alvo = r.tipo === 'licitacao' ? `lic:${r.oportunidade_id}`
      : r.tipo === 'negocio' ? `neg:${r.negocio_id}` : `livre:${r.id}`;
    if (!porAlvo.has(alvo)) porAlvo.set(alvo, []);
    porAlvo.get(alvo)!.push(r);
  }

  const saida: ItemPainel[] = [];
  const editaisNoRadar = new Set<number>();
  porAlvo.forEach((linhas) => {
    // O MEU primeiro; entre os dos outros, o de quem pôs antes.
    const ordenadas = [...linhas].sort((a, b) =>
      (Number(!!b.meu) - Number(!!a.meu))
      || String(a.criado_em || '').localeCompare(String(b.criado_em || ''))
      || (a.id - b.id));
    const exibida = ordenadas[0];
    const quadro = exibida.tipo === 'licitacao' && exibida.oportunidade_id != null
      ? quadroPorEdital.get(Number(exibida.oportunidade_id)) : undefined;
    if (exibida.tipo === 'licitacao' && exibida.oportunidade_id != null) {
      editaisNoRadar.add(Number(exibida.oportunidade_id));
    }
    saida.push(deRadar(exibida, ordenadas.slice(1), quadro));
  });
  // Licitação do quadro que ninguém pôs no radar entra assim mesmo: o
  // pipeline também é o radar.
  for (const l of p.pipeline || []) {
    if (!editaisNoRadar.has(Number(l.oportunidade_id))) saida.push(doQuadro(l));
  }
  for (const a of p.atividades || []) saida.push(daAtividade(a));
  return saida;
}

export type FiltroTipo = 'todos' | TipoPainel;
/** 'todos', 'eu' ou o e-mail de uma pessoa. */
export type FiltroPessoa = string;

export function filtrarPainel(
  itens: ItemPainel[], tipo: FiltroTipo, pessoa: FiltroPessoa, eu: string,
): ItemPainel[] {
  const quem = pessoa === 'eu' ? minusculo(eu) : minusculo(pessoa);
  return itens.filter(i =>
    (tipo === 'todos' || i.tipo === tipo)
    && (pessoa === 'todos' || !quem || i.pessoas.includes(quem)));
}

/** Agenda: o que vence primeiro; sem prazo no fim. */
export function ordenarPorPrazo(itens: ItemPainel[]): ItemPainel[] {
  const t = (i: ItemPainel) => {
    const v = i.prazo ? new Date(i.prazo).getTime() : NaN;
    return Number.isFinite(v) ? v : Number.POSITIVE_INFINITY;
  };
  return [...itens].sort((a, b) => (t(a) - t(b)) || a.chave.localeCompare(b.chave));
}

/** Nome para a tela: o do cadastro, senão o começo do e-mail. */
export function nomeDaPessoa(email: string | null | undefined, pessoas: Record<string, string>): string {
  const e = minusculo(email);
  if (!e) return '—';
  const nome = pessoas[e];
  if (nome && nome.trim()) return nome.trim();
  return e.split('@')[0];
}

/** As pessoas do filtro: eu primeiro, depois por nome. */
export function pessoasDoFiltro(
  itens: ItemPainel[], pessoas: Record<string, string>, eu: string,
): Array<{ email: string; nome: string; total: number }> {
  const contagem = new Map<string, number>();
  for (const i of itens) for (const e of i.pessoas) contagem.set(e, (contagem.get(e) || 0) + 1);
  const meu = minusculo(eu);
  return Array.from(contagem.entries())
    .filter(([e]) => e !== meu)
    .map(([email, total]) => ({ email, nome: nomeDaPessoa(email, pessoas), total }))
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
}

const EMOJI_ATIVIDADE: Record<string, string> = {
  ligacao: '📞', reuniao: '📅', email: '✉️', tarefa: '✅', follow_up: '🔁', prazo: '⏳',
  visita: '🚗', nota: '📝',
};
const ROTULO_ATIVIDADE: Record<string, string> = {
  ligacao: 'Ligação', reuniao: 'Reunião', email: 'E-mail', tarefa: 'Tarefa', follow_up: 'Follow-up',
  prazo: 'Prazo', visita: 'Visita', nota: 'Nota',
};

/** Emoji e rótulo da ATIVIDADE pelo tipo dela. */
export function marcaDaAtividade(tipo: string | null | undefined): { emoji: string; rotulo: string } {
  const t = minusculo(tipo);
  return { emoji: EMOJI_ATIVIDADE[t] || '✅', rotulo: ROTULO_ATIVIDADE[t] || 'Atividade' };
}

/** A linha "de quem é" do cartão — regra 2: o que não é meu diz de quem é. */
export function deQuemE(item: ItemPainel, pessoas: Record<string, string>): string | null {
  const nome = (e: string | null) => nomeDaPessoa(e, pessoas);
  if (item.fonte === 'atividade') {
    const r = item.atividade?.responsaveis?.length ? item.atividade.responsaveis : [item.atividade?.criado_por || null];
    return `Atividade de ${r.filter(Boolean).map(e => nome(e as string)).join(', ') || '—'}`;
  }
  if (item.fonte === 'pipeline') {
    return item.responsavel_email ? `No pipeline · responsável ${nome(item.responsavel_email)}` : 'No pipeline · sem responsável';
  }
  const partes: string[] = [];
  if (!item.meu && item.dono_email) partes.push(`No radar de ${nome(item.dono_email)}`);
  if (item.acompanhantes.length) {
    partes.push(`também no radar de ${item.acompanhantes.map(nome).join(', ')}`);
  }
  if (item.etapa_rotulo) {
    partes.push(`no pipeline${item.responsavel_email ? ` · responsável ${nome(item.responsavel_email)}` : ''}`);
  }
  if (!partes.length) return null;
  const frase = partes.join(' · ');
  return frase.charAt(0).toUpperCase() + frase.slice(1);
}
